import {
  RECEIPT_DETAIL_FIELDS,
  RECEIPT_TEMPLATE_KEYS,
  ReceiptDetailField,
  ReceiptTemplateKey,
  type ReceiptConfig,
  type ReceiptConfigDefaults,
} from "@/features/finance/model/finance-types"

export const DEFAULT_RECEIPT_CONFIG: ReceiptConfig = {
  templateKey: ReceiptTemplateKey.Classic,
  organizationNameOverride: "",
  branchNameOverride: "",
  headerSummary: "",
  notesText: "",
  footerText: "",
  footerContacts: "",
  accentColor: null,
  signatureLabel: "",
  signedByName: "",
  signedByTitle: "",
  signatureSvg: null,
}

export const EMPTY_RECEIPT_DEFAULTS: ReceiptConfigDefaults = {
  organizationName: "",
  branchName: "",
  headerSummary: "",
  footerContacts: "",
}

export function normalizeReceiptConfig(config: ReceiptConfig): ReceiptConfig {
  return {
    ...DEFAULT_RECEIPT_CONFIG,
    ...config,
    // Keep the SAVED template. This used to pin Classic unconditionally -- a leftover from when the key was
    // decorative and every value rendered the same document -- so choosing Compact or Formal saved fine and
    // then snapped back to Classic the moment the config reloaded.
    templateKey: RECEIPT_TEMPLATE_KEYS.includes(config.templateKey) ? config.templateKey : ReceiptTemplateKey.Classic,
    organizationNameOverride: config.organizationNameOverride ?? "",
    branchNameOverride: config.branchNameOverride ?? "",
    headerSummary: config.headerSummary ?? "",
    notesText: config.notesText ?? "",
    footerText: config.footerText ?? "",
    footerContacts: config.footerContacts ?? "",
    accentColor: null,
    signatureLabel: config.signatureLabel ?? "",
    signedByName: config.signedByName ?? "",
    signedByTitle: config.signedByTitle ?? "",
    signatureSvg: config.signatureSvg ?? null,
  }
}

/**
 * Prefill an unconfigured receipt with what the workspace already knows.
 *
 * A branch that never opens this page should still issue a receipt that names the church, its contact details
 * and who signs, so the defaults are written into the DRAFT rather than left as placeholder text. Placeholders
 * look identical to real values on screen and then save as empty, which is how a receipt reaches a member with
 * no contact line on it.
 *
 * `||` not `??`: an empty string is "never set", not a deliberate blank. Anything the user actually typed wins.
 */
export function populateReceiptConfigDefaults(
  config: ReceiptConfig,
  defaults: ReceiptConfigDefaults,
  text: { signatureLabel: string; thankYou: string },
): ReceiptConfig {
  return {
    ...config,
    organizationNameOverride: config.organizationNameOverride || defaults.organizationName,
    branchNameOverride: config.branchNameOverride || defaults.branchName,
    headerSummary: config.headerSummary || defaults.headerSummary,
    footerContacts: config.footerContacts || defaults.footerContacts,
    // Not derived from workspace data, but every receipt wants them and both have one obvious answer. Filled
    // from the translated copy so a French workspace does not start with an English receipt.
    signatureLabel: config.signatureLabel || text.signatureLabel,
    notesText: config.notesText || text.thankYou,
  }
}

/**
 * Whether a row prints. An unset list means "everything", matching every receipt issued before this setting
 * existed, so an untouched branch sees all switches on rather than a blank form that would strip its receipts.
 */
export function isDetailFieldEnabled(config: ReceiptConfig, field: ReceiptDetailField): boolean {
  return config.detailFields ? config.detailFields.includes(field) : true
}

/** The next selection. Always an explicit array, so turning the LAST row off is recorded rather than read as unset. */
export function toggleDetailField(config: ReceiptConfig, field: ReceiptDetailField, enabled: boolean): ReceiptDetailField[] {
  const current = config.detailFields ?? RECEIPT_DETAIL_FIELDS
  const next = new Set(current)
  if (enabled) next.add(field)
  else next.delete(field)
  // Canonical order, never click order: this is also the order they print in.
  return RECEIPT_DETAIL_FIELDS.filter((entry) => next.has(entry))
}
